import { useEffect, useRef, useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Dialog, DialogContent } from "./ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";
import { CaretLeft, CaretRight, CaretDoubleLeft, CaretDoubleRight, ShieldCheck, Calendar, Camera, ArrowCounterClockwise, Lock, Eye, Warning, DeviceMobile } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

function fmtDt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
}

function questionOptions(q) {
    if (q.type === "for_against") return ["FOR", "AGAINST"];
    if (q.type === "yes_no") return ["YES", "NO"];
    if (q.type === "rating") return ["1","2","3","4","5","6","7","8","9","10"];
    if (q.type === "text_response") return [];
    return q.options || [];
}

function isMultiSelect(q) {
    return q.type === "multi" && parseInt(q.max_selections || 1, 10) > 1;
}

const TEXT_RESPONSE_MAX_LEN = 500;

export default function BallotWizard({ open, election, onSubmit, onExit, previewOnly = false }) {
    const candidates = election?.candidates || [];
    const questions = election?.questions || [];

    // Build the list of "items" the wizard pages through.
    // If the election has candidates, the FIRST item is the candidate-selection step.
    const items = useMemo(() => {
        const out = [];
        if (candidates.length > 0) {
            out.push({ kind: "candidate", id: "__candidate__" });
        }
        questions.forEach((q) => out.push({ kind: "question", q, id: q.id }));
        return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [election?.id]);

    const [idx, setIdx] = useState(0);
    const [candidateId, setCandidateId] = useState("");
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [showFaceCapture, setShowFaceCapture] = useState(false);
    const [showIncompleteWarn, setShowIncompleteWarn] = useState(false);
    const [noCameraDetected, setNoCameraDetected] = useState(false);

    // For secure elections, proactively detect if the current device has a
    // usable camera. If not, we show a bold red banner up-top and block the
    // submit button so the voter doesn't discover this only after answering
    // the entire ballot.
    useEffect(() => {
        if (!open || previewOnly || !election?.secure_election) {
            setNoCameraDetected(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                if (!navigator.mediaDevices?.enumerateDevices) {
                    if (!cancelled) setNoCameraDetected(true);
                    return;
                }
                const devs = await navigator.mediaDevices.enumerateDevices();
                const hasCam = devs.some((d) => d.kind === "videoinput");
                if (!cancelled) setNoCameraDetected(!hasCam);
            } catch {
                if (!cancelled) setNoCameraDetected(true);
            }
        })();
        return () => { cancelled = true; };
    }, [open, previewOnly, election?.secure_election, election?.id]);

    const total = items.length;
    const answeredCount = useMemo(() => {
        let n = Object.keys(answers).filter((k) => {
            const v = answers[k];
            if (Array.isArray(v)) return v.length > 0;
            return typeof v === "string" ? v.trim().length > 0 : !!v;
        }).length;
        if (candidates.length > 0 && candidateId) n += 1;
        return n;
    }, [answers, candidateId, candidates.length]);
    const pct = total > 0 ? Math.round((answeredCount / total) * 100) : 0;
    const current = items[idx];

    if (!election || total === 0) return null;

    const setAnswer = (qid, opt) => setAnswers((a) => ({ ...a, [qid]: opt }));

    const goto = (i) => {
        if (i < 0) i = 0;
        if (i > total - 1) i = total - 1;
        setIdx(i);
    };

    const submit = async (facePhoto = null) => {
        setSubmitting(true);
        // Trim text answers, keep arrays for multi-select, drop empties.
        const cleanAnswers = {};
        Object.keys(answers).forEach((k) => {
            const v = answers[k];
            if (Array.isArray(v)) {
                const filtered = v.filter((x) => x != null && String(x).length > 0);
                if (filtered.length > 0) cleanAnswers[k] = filtered;
            } else if (typeof v === "string") {
                const t = v.trim();
                if (t) cleanAnswers[k] = t;
            } else if (v) {
                cleanAnswers[k] = v;
            }
        });
        await onSubmit?.({ candidate_id: candidateId || null, answers: cleanAnswers, face_photo: facePhoto });
        setSubmitting(false);
    };

    const onSubmitClick = () => {
        if (previewOnly) {
            onExit?.();
            return;
        }
        // Warn the voter if any ballot question is unanswered. We treat
        // candidate selection separately (it's already gated by submitDisabled).
        const unanswered = questions.filter((q) => {
            const v = answers[q.id];
            if (Array.isArray(v)) return v.length === 0;
            return typeof v === "string" ? v.trim().length === 0 : !v;
        });
        if (unanswered.length > 0) {
            setShowIncompleteWarn(true);
            return;
        }
        proceedSubmit();
    };

    const proceedSubmit = () => {
        setShowIncompleteWarn(false);
        if (election.secure_election) {
            setShowFaceCapture(true);
        } else {
            submit();
        }
    };

    const unansweredQuestions = questions.filter((q) => {
        const v = answers[q.id];
        if (Array.isArray(v)) return v.length === 0;
        return typeof v === "string" ? v.trim().length === 0 : !v;
    });

    const candidateRequired = candidates.length > 0;
    const cameraBlocked = !previewOnly && !!election.secure_election && noCameraDetected;
    const submitDisabled = submitting || (!previewOnly && candidateRequired && !candidateId) || cameraBlocked;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onExit?.()}>
            <DialogContent className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-3xl max-h-[94vh] overflow-y-auto p-0 gap-0" data-testid="ballot-wizard">
                {/* Header bar */}
                <div className={`${previewOnly ? "bg-purple-700" : "bg-civic-700"} text-white px-4 sm:px-6 py-4 sm:py-5 rounded-t-md`}>
                    {previewOnly && (
                        <div className="inline-flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-widest font-bold bg-white/20 border border-white/30 rounded px-2 py-0.5" data-testid="ballot-preview-badge">
                            <Eye size={12} weight="bold" /> Admin Preview · Read-Only
                        </div>
                    )}
                    <div className="text-[10px] sm:text-xs uppercase tracking-widest text-white/80 font-semibold truncate">{election.authority || "Election Authority"}</div>
                    <h2 className="font-heading font-black text-lg sm:text-2xl mt-1 tracking-tight break-words leading-tight pr-6">{election.title}</h2>
                    <div className={`hash-mono text-[10px] sm:text-xs ${previewOnly ? "text-white/80" : "text-civic-100"} mt-2 flex flex-wrap gap-x-3 sm:gap-x-5 gap-y-1`}>
                        <span className="flex items-center gap-1.5 min-w-0"><Calendar size={12} weight="bold" className="shrink-0" /> <span className="truncate">{fmtDt(election.starts_at)} → {fmtDt(election.ends_at)}</span></span>
                    </div>
                </div>

                {/* Progress strip */}
                <div className="border-b border-slate-200 bg-white px-4 sm:px-6 py-3 sm:py-4">
                    <div className="flex items-center justify-between gap-2 text-xs sm:text-sm mb-2">
                        <div className="text-slate-600 min-w-0 truncate">
                            <span className="font-semibold text-slate-900">{answeredCount}</span> of {total} answered
                        </div>
                        <div className="text-slate-600 font-semibold shrink-0">{pct}%</div>
                    </div>
                    <div className="h-2 rounded-sm bg-slate-100 overflow-hidden">
                        <motion.div initial={false} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }}
                            className="h-full bg-emerald-500" />
                    </div>
                    {/* Dot indicators */}
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="ballot-dots">
                        {items.map((it, i) => {
                            const rawAns = it.kind === "candidate" ? candidateId : answers[it.id];
                            const answered = it.kind === "candidate"
                                ? !!candidateId
                                : (Array.isArray(rawAns) ? rawAns.length > 0
                                    : (typeof rawAns === "string" ? rawAns.trim().length > 0 : !!rawAns));
                            const active = i === idx;
                            const label = it.kind === "candidate" ? "Candidate selection" : `#${i + 1} · ${it.q.title}`;
                            return (
                                <button
                                    key={it.id}
                                    type="button"
                                    onClick={() => goto(i)}
                                    title={label}
                                    className={`h-2.5 w-2.5 rounded-full transition-all ${
                                        active ? "ring-2 ring-civic-600 ring-offset-1 ring-offset-white"
                                        : ""
                                    } ${
                                        answered ? "bg-emerald-500" : "bg-slate-300 hover:bg-slate-400"
                                    }`}
                                    data-testid={`ballot-dot-${i}`}
                                />
                            );
                        })}
                    </div>
                </div>

                {/* Camera-required warning — shown only for secure elections
                    when the current device has no video input. Bold, blocking. */}
                {cameraBlocked && (
                    <div
                        className="border-b-4 border-rose-700 bg-rose-600 text-white px-4 sm:px-6 py-4 sm:py-5"
                        role="alert"
                        data-testid="ballot-no-camera-warning"
                    >
                        <div className="flex items-start gap-3">
                            <div className="shrink-0 rounded-full bg-white/20 border border-white/40 p-2">
                                <Warning size={26} weight="fill" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[11px] uppercase tracking-widest font-black text-rose-100">
                                    Camera Required
                                </div>
                                <h3 className="font-heading font-black text-xl sm:text-2xl leading-tight mt-0.5">
                                    This device has no camera — you cannot vote here.
                                </h3>
                                <p className="mt-2 text-sm sm:text-base font-semibold text-rose-50 leading-relaxed">
                                    This is a <span className="underline underline-offset-2">secure election</span> that requires a photo of the voter at submission. Please switch to a phone, tablet, or laptop with a working camera to cast your ballot.
                                </p>
                                <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest bg-white/15 border border-white/30 rounded px-2 py-1">
                                    <DeviceMobile size={13} weight="bold" /> Try again on a device with a camera
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step body */}
                <div className="px-4 sm:px-6 py-4 sm:py-6">
                    <div className="text-[10px] sm:text-xs uppercase tracking-widest text-civic-700 font-semibold mb-2">
                        {current.kind === "candidate"
                            ? `Candidate Selection · Step ${idx + 1} of ${total}`
                            : `Proposition ${idx + 1} of ${total}`}
                    </div>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={current.id}
                            initial={{ opacity: 0, x: 12 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -12 }}
                            transition={{ duration: 0.18 }}
                        >
                            {current.kind === "candidate" ? (
                                <Card className="p-4 sm:p-6 bg-white border-slate-200" data-testid="ballot-step-card-candidate">
                                    <h3 className="font-heading font-bold text-lg sm:text-2xl text-slate-900 leading-tight">Choose a candidate</h3>
                                    <p className="mt-2 text-sm sm:text-base text-slate-700">Select one candidate. This selection is required and final.</p>
                                    <div className={`mt-5 sm:mt-6 grid gap-3 ${candidates.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1"}`}>
                                        {candidates.map((c) => {
                                            const picked = candidateId === c.id;
                                            return (
                                                <button
                                                    key={c.id}
                                                    type="button"
                                                    onClick={() => setCandidateId(c.id)}
                                                    className={`text-left p-4 rounded-md border-2 transition-all ${
                                                        picked
                                                            ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200"
                                                            : "border-slate-200 bg-white hover:border-civic-400"
                                                    }`}
                                                    data-testid={`ballot-candidate-${c.id}`}
                                                >
                                                    <div className="font-semibold text-slate-900 text-base break-words">{c.name}</div>
                                                    {c.party && <div className="text-xs hash-mono text-civic-700 mt-0.5 break-words">{c.party}</div>}
                                                    {c.bio && <div className="text-sm text-slate-600 mt-1 break-words">{c.bio}</div>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </Card>
                            ) : (
                                <Card className="p-4 sm:p-6 bg-white border-slate-200" data-testid={`ballot-question-${current.q.id}`}>
                                    <h3 className="font-heading font-bold text-lg sm:text-2xl text-slate-900 leading-tight break-words">{current.q.title}</h3>
                                    <p className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed break-words">{current.q.text}</p>

                                    {current.q.type === "text_response" ? (
                                        <div className="mt-5 sm:mt-6" data-testid="ballot-text-response-wrap">
                                            <textarea
                                                rows={5}
                                                maxLength={TEXT_RESPONSE_MAX_LEN}
                                                value={answers[current.q.id] || ""}
                                                onChange={(e) => setAnswer(current.q.id, e.target.value)}
                                                placeholder="Type your answer here…"
                                                disabled={previewOnly}
                                                className="w-full rounded-md border-2 border-slate-200 focus:border-civic-500 focus:ring-1 focus:ring-civic-200 bg-white p-3 text-base leading-relaxed outline-none transition-colors"
                                                data-testid={`ballot-text-input-${current.q.id}`}
                                            />
                                            <div className="mt-1.5 flex justify-end text-[11px] hash-mono text-slate-500">
                                                <span data-testid={`ballot-text-count-${current.q.id}`}>{(answers[current.q.id] || "").length}/{TEXT_RESPONSE_MAX_LEN}</span>
                                            </div>
                                        </div>
                                    ) : current.q.type === "rating" ? (
                                        <div className="mt-5 sm:mt-6" data-testid="ballot-rating-wrap">
                                            {/* Desktop: one horizontal bar with left/right labels above.
                                                Mobile: labels anchor to the actual grid corners so the
                                                1↔10 scale still reads intuitively when buttons wrap
                                                into two rows. */}
                                            <div className="hidden sm:flex items-center justify-between text-[11px] uppercase tracking-widest text-slate-500 mb-2">
                                                <span>1 · Strongly disagree</span>
                                                <span>10 · Strongly agree</span>
                                            </div>
                                            <div className="sm:hidden flex items-start justify-between gap-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                                                <span className="flex items-center gap-1 text-rose-600"><span className="hash-mono font-black text-sm text-rose-700">1</span> Disagree</span>
                                                <span className="flex items-center gap-1 text-emerald-600">Agree <span className="hash-mono font-black text-sm text-emerald-700">10</span></span>
                                            </div>
                                            <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5 sm:gap-2" data-testid="ballot-options">
                                                {questionOptions(current.q).map((opt) => {
                                                    const picked = answers[current.q.id] === opt;
                                                    return (
                                                        <button
                                                            key={opt}
                                                            type="button"
                                                            onClick={() => setAnswer(current.q.id, opt)}
                                                            className={`h-11 sm:h-12 min-w-0 rounded-md border-2 font-heading font-bold text-base sm:text-lg transition-all ${
                                                                picked
                                                                    ? "border-emerald-500 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                                                                    : "border-slate-200 bg-white text-slate-700 hover:border-civic-400"
                                                            }`}
                                                            data-testid={`ballot-option-${current.q.id}-${opt}`}
                                                        >
                                                            {opt}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {/* Mobile-only: full-width gradient hint under the grid so
                                                the visual continuity of the 1→10 scale survives the
                                                two-row wrap. */}
                                            <div className="sm:hidden mt-2 h-1 rounded-full bg-gradient-to-r from-rose-400 via-amber-300 to-emerald-500" aria-hidden="true" />
                                        </div>
                                    ) : isMultiSelect(current.q) ? (
                                        (() => {
                                            const maxSel = parseInt(current.q.max_selections || 1, 10);
                                            const picks = Array.isArray(answers[current.q.id]) ? answers[current.q.id] : [];
                                            const atLimit = picks.length >= maxSel;
                                            return (
                                                <div className="mt-5 sm:mt-6" data-testid="ballot-multi-wrap">
                                                    <div className="flex items-center justify-between text-[11px] uppercase tracking-widest mb-2">
                                                        <span className="text-slate-500">Pick up to {maxSel}</span>
                                                        <span className={`hash-mono ${atLimit ? "text-amber-700 font-bold" : "text-slate-500"}`} data-testid={`ballot-multi-count-${current.q.id}`}>{picks.length}/{maxSel} selected</span>
                                                    </div>
                                                    <div className="grid gap-3 sm:grid-cols-1" data-testid="ballot-options">
                                                        {questionOptions(current.q).map((opt) => {
                                                            const picked = picks.includes(opt);
                                                            const disabled = !picked && atLimit;
                                                            return (
                                                                <button
                                                                    key={opt}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (previewOnly) return;
                                                                        if (picked) {
                                                                            setAnswer(current.q.id, picks.filter((p) => p !== opt));
                                                                        } else if (!atLimit) {
                                                                            setAnswer(current.q.id, [...picks, opt]);
                                                                        }
                                                                    }}
                                                                    disabled={disabled}
                                                                    aria-pressed={picked}
                                                                    title={disabled ? `You can only pick ${maxSel}. Deselect one to change your choice.` : undefined}
                                                                    className={`text-left p-4 rounded-md border-2 transition-all flex items-center gap-3 ${
                                                                        picked
                                                                            ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200"
                                                                            : disabled
                                                                                ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                                                                                : "border-slate-200 bg-white hover:border-civic-400"
                                                                    }`}
                                                                    data-testid={`ballot-option-${current.q.id}-${opt}`}
                                                                >
                                                                    <span
                                                                        aria-hidden="true"
                                                                        className={`flex items-center justify-center h-5 w-5 rounded border-2 transition-all shrink-0 ${
                                                                            picked ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-300"
                                                                        }`}
                                                                    >
                                                                        {picked && (
                                                                            <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
                                                                                <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
                                                                            </svg>
                                                                        )}
                                                                    </span>
                                                                    <span className="font-semibold text-slate-900 text-base break-words min-w-0">{opt}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        <div className={`mt-5 sm:mt-6 grid gap-3 ${questionOptions(current.q).length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1"}`}
                                             data-testid="ballot-options">
                                            {questionOptions(current.q).map((opt) => {
                                                const picked = answers[current.q.id] === opt;
                                                return (
                                                    <button
                                                        key={opt}
                                                        type="button"
                                                        onClick={() => setAnswer(current.q.id, opt)}
                                                        className={`text-left p-4 rounded-md border-2 transition-all ${
                                                            picked
                                                                ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200"
                                                                : "border-slate-200 bg-white hover:border-civic-400"
                                                        }`}
                                                        data-testid={`ballot-option-${current.q.id}-${opt}`}
                                                    >
                                                        <span className="font-semibold text-slate-900 text-base break-words">{opt}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </Card>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Footer nav */}
                <div className="border-t border-slate-200 bg-slate-50 px-3 sm:px-6 py-3 sm:py-4 rounded-b-md">
                    <div className="flex items-center justify-between gap-2 sm:gap-3 flex-wrap">
                        <div className="flex items-center gap-1">
                            <Button size="icon" variant="outline" onClick={() => goto(0)} disabled={idx === 0} data-testid="ballot-first">
                                <CaretDoubleLeft size={16} />
                            </Button>
                            <Button size="icon" variant="outline" onClick={() => goto(idx - 1)} disabled={idx === 0} data-testid="ballot-prev">
                                <CaretLeft size={16} />
                            </Button>
                        </div>
                        <div className="hash-mono text-sm text-slate-700">
                            Step <span className="font-semibold">{idx + 1}</span> of {total}
                        </div>
                        <div className="flex items-center gap-1">
                            {(() => {
                                // Highlight the "next" button once the current step is answered
                                // so voters get a visual push toward the next selection.
                                const rawAns = current.kind === "candidate" ? candidateId : answers[current.id];
                                const stepAnswered = current.kind === "candidate"
                                    ? !!candidateId
                                    : (Array.isArray(rawAns) ? rawAns.length > 0
                                        : (typeof rawAns === "string" ? rawAns.trim().length > 0 : !!rawAns));
                                const canAdvance = idx < total - 1 && stepAnswered;
                                return (
                                    <Button
                                        size="icon"
                                        variant={canAdvance ? "default" : "outline"}
                                        onClick={() => goto(idx + 1)}
                                        disabled={idx === total - 1}
                                        aria-label="Next"
                                        className={canAdvance
                                            ? "h-11 w-11 bg-emerald-600 hover:bg-emerald-700 text-white ring-2 ring-emerald-200 shadow-md transition-all"
                                            : "transition-all"}
                                        data-testid="ballot-next"
                                    >
                                        <CaretRight size={canAdvance ? 24 : 16} weight={canAdvance ? "bold" : "regular"} />
                                    </Button>
                                );
                            })()}
                            <Button size="icon" variant="outline" onClick={() => goto(total - 1)} disabled={idx === total - 1} data-testid="ballot-last">
                                <CaretDoubleRight size={16} />
                            </Button>
                        </div>
                    </div>
                    <Button
                        type="button"
                        onClick={onSubmitClick}
                        disabled={submitDisabled}
                        className={`mt-3 w-full h-12 text-white text-sm sm:text-base whitespace-normal leading-tight px-3 ${previewOnly ? "bg-purple-600 hover:bg-purple-700" : "bg-civic-600 hover:bg-civic-700"}`}
                        data-testid="ballot-submit"
                    >
                        {previewOnly ? (
                            <><Eye size={18} className="mr-2 shrink-0" weight="fill" /> Close preview</>
                        ) : (
                            <>
                                {election.secure_election ? <Camera size={18} className="mr-2 shrink-0" weight="fill" /> : <ShieldCheck size={18} className="mr-2 shrink-0" weight="fill" />}
                                {submitting ? "Sealing on chain…"
                                 : cameraBlocked ? "Camera required — switch devices"
                                 : candidateRequired && !candidateId ? "Select a candidate to continue"
                                 : election.secure_election
                                    ? (answeredCount < total ? `Take Photo & Submit (${answeredCount}/${total})` : "Take Photo & Submit")
                                    : answeredCount < total ? `Submit anyway · ${answeredCount}/${total} answered`
                                    : "Submit & Seal on Chain"}
                            </>
                        )}
                    </Button>
                </div>
            </DialogContent>

            {/* Secure election: face capture step */}
            <FaceCaptureDialog
                open={showFaceCapture}
                onClose={() => setShowFaceCapture(false)}
                onConfirm={(dataUrl) => { setShowFaceCapture(false); submit(dataUrl); }}
            />

            {/* Incomplete-ballot confirmation — voter may proceed with blanks. */}
            <AlertDialog open={showIncompleteWarn} onOpenChange={(o) => !o && setShowIncompleteWarn(false)}>
                <AlertDialogContent data-testid="ballot-incomplete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 font-heading text-2xl text-amber-700">
                            <Warning size={22} weight="fill" /> Your ballot is not complete
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-700 text-sm sm:text-base leading-relaxed">
                            You have <span className="font-bold text-slate-900">{unansweredQuestions.length}</span>{" "}
                            unanswered {unansweredQuestions.length === 1 ? "question" : "questions"}. Blank answers will not be counted on the blockchain.
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    {unansweredQuestions.length > 0 && (
                        <div className="rounded-md border-2 border-amber-200 bg-amber-50 p-3 sm:p-4 max-h-48 overflow-y-auto" data-testid="ballot-incomplete-list">
                            <div className="text-[11px] uppercase tracking-widest text-amber-800 font-bold mb-2">Unanswered items</div>
                            <ul className="space-y-1.5 text-sm text-amber-900">
                                {unansweredQuestions.map((q) => {
                                    const stepIdx = items.findIndex((it) => it.kind === "question" && it.id === q.id);
                                    return (
                                        <li key={q.id} className="flex gap-2">
                                            <span className="hash-mono font-bold shrink-0">#{stepIdx + 1}</span>
                                            <span className="font-medium">{q.title}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel data-testid="ballot-incomplete-back" className="mt-0">
                            Go back and finish
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="ballot-incomplete-proceed"
                            onClick={proceedSubmit}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                            Submit anyway
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
}

function FaceCaptureDialog({ open, onClose, onConfirm }) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [error, setError] = useState(null);
    const [snapshot, setSnapshot] = useState(null);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            setError(null); setSnapshot(null);
            try {
                const s = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
                    audio: false,
                });
                if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
                setStream(s);
                if (videoRef.current) {
                    videoRef.current.srcObject = s;
                    await videoRef.current.play().catch(() => {});
                }
            } catch (ex) {
                setError(ex?.message || "Camera unavailable. Please allow camera access and try again.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open]);

    useEffect(() => {
        return () => {
            if (stream) stream.getTracks().forEach((t) => t.stop());
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stream]);

    const takePhoto = () => {
        const v = videoRef.current; const c = canvasRef.current;
        if (!v || !c) return;
        const w = v.videoWidth || 640; const h = v.videoHeight || 480;
        c.width = w; c.height = h;
        const ctx = c.getContext("2d");
        ctx.drawImage(v, 0, 0, w, h);
        const url = c.toDataURL("image/jpeg", 0.75);
        setSnapshot(url);
    };

    const retake = () => setSnapshot(null);
    const confirm = () => { if (snapshot) onConfirm(snapshot); };

    const handleClose = () => {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        setStream(null); setSnapshot(null);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
            <DialogContent className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-md p-0 gap-0 overflow-hidden" data-testid="face-capture-dialog">
                <div className="bg-rose-600 text-white px-5 py-3">
                    <div className="flex items-center gap-2">
                        <Lock size={16} weight="fill" />
                        <h3 className="font-heading font-bold text-base">Secure Election — Photo Required</h3>
                    </div>
                    <p className="text-[11px] text-rose-100 mt-0.5 leading-snug">Your photo will appear on the public "Who voted" log alongside your name and precinct.</p>
                </div>

                <div className="p-4 bg-slate-50">
                    <div className="aspect-[4/3] w-full rounded-md overflow-hidden bg-black grid place-items-center relative">
                        {error ? (
                            <div className="w-full h-full bg-rose-600 text-white p-5 flex flex-col items-center justify-center text-center gap-2" data-testid="face-error">
                                <Warning size={40} weight="fill" />
                                <div className="font-heading font-black text-lg leading-tight">Camera unavailable</div>
                                <div className="text-sm font-semibold text-rose-50 leading-snug max-w-xs">
                                    {error}
                                </div>
                                <div className="mt-1 text-[11px] uppercase tracking-widest font-bold bg-white/15 border border-white/30 rounded px-2 py-1">
                                    Switch to a device with a camera
                                </div>
                            </div>
                        ) : snapshot ? (
                            <img src={snapshot} alt="Captured selfie" className="w-full h-full object-cover" data-testid="face-snapshot" />
                        ) : (
                            <video ref={videoRef} className="w-full h-full object-cover" muted playsInline data-testid="face-video" />
                        )}
                        <canvas ref={canvasRef} className="hidden" />
                    </div>

                    <div className="mt-3 flex gap-2 justify-between">
                        <Button variant="ghost" onClick={handleClose} className="text-slate-600" data-testid="face-cancel">Cancel</Button>
                        {snapshot ? (
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={retake} data-testid="face-retake">
                                    <ArrowCounterClockwise size={14} className="mr-1.5" weight="bold" /> Retake
                                </Button>
                                <Button onClick={confirm} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="face-confirm">
                                    <ShieldCheck size={14} className="mr-1.5" weight="fill" /> Submit Vote
                                </Button>
                            </div>
                        ) : (
                            <Button onClick={takePhoto} disabled={!!error} className="bg-civic-600 hover:bg-civic-700 text-white" data-testid="face-take">
                                <Camera size={14} className="mr-1.5" weight="fill" /> Take Photo
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
